import React from 'react';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { change, Field, FieldArray, reduxForm } from 'redux-form';

import { Typography } from '@material-ui/core';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';

import {
  AppHeader,
  CircularLoader,
  SingleDatePicker,
  MultiDatePickerCalendar,
  DropDown,
  CheckboxButtonsGroup,
  FormActionButtons,
  BookInfoList,
  CustomTimePicker
} from 'components';

import ConfirmContext from 'contexts/ConfirmContext';

import {
  CREATE_RENTAL_ITEMS_BOOKING,
  UPDATE_RENTAL_ITEMS_BOOKING
} from 'graphql/mutations/rental-items-bookings';
import {
  RENTAL_ITEMS_AVAILABLE_UNITS,
  RENTAL_ITEM
} from 'graphql/queries/rental-items';

import {
  getMaxUnits,
  getMaxUnitsRentalItemsSeveralDays,
  getMaxUnitsRentalItemsHourly,
  getBookedUnitsHourly,
  getBookedUnits
} from 'utils/book-rental-items/maxAvailableUnits';

import {getPreparedBookRentalItemsData} from 'utils/book-rental-items/prepareBookRentalItemsData';
import { getReceivedRentalItemsData,getReceivedRentalItemsBookingsDates } from 'utils/rental-items/prepareReceivedRentalItemsData';
import UnitsInputField from './components/available-units-filed/AvailableUnitsField';
import { getDisabledDates,getDisabledDatesWithTime } from 'utils/book-rental-items/datesAvailability';

import AddOnsList from 'pages/book-activity/components/add-ons-list/AddOnsList';
import GuestInformation from 'pages/book-activity//components/guest-information/GuestInformation';
import RentalBookingSummary from './components/book-summary/BookingSummary';
import { getListOfAvailableCurrencies } from 'utils/book-accommodation-and-activity/currency';
import {getAvailableUnits}   from 'utils/book-rental-items/prepareAvailableUnits';

import useStyles from 'pages/book-activity/styles';
import validate from './validate';

const getInitialValues = (bookedData) => {
  const baseValues = {
    currency: 'NOK',
    addOns: [],
    availableUnits: [],
    maxAvailableUnits: 0,
    startTime: null,
    endTime: null,
    minMaxHours: null,
    minDays: null,
    maxDays : null,
    pay: {
      'Pay on arrival': true
    }
  };

  if (!bookedData) return baseValues;

  if (bookedData.startTime && bookedData.startTime !== "Invalid date") {
    try {
      baseValues.startTime = parseTimeOnlyString(bookedData.startTime, bookedData.startingDate || bookedData.multiDates?.startDate);
    } catch (error) {
      baseValues.startTime = null;
    }
  }
  
  if (bookedData.endTime && bookedData.endTime !== "Invalid date") {
    try {
      baseValues.endTime = parseTimeOnlyString(bookedData.endTime, bookedData.startingDate || bookedData.multiDates?.startDate);
    } catch (error) {
      baseValues.endTime = null;
    }
  }

  if (bookedData.multiDates) {
    baseValues.multiDates = {
      startDate: moment(bookedData.multiDates.startDate),
      endDate: moment(bookedData.multiDates.endDate)
    };
  } else if (bookedData.startingDate) {
    baseValues.startingDate = moment(bookedData.startingDate);
  }

  return baseValues;
};


const parseTimeOnlyString = (timeString, date) => {
  if (!timeString || timeString === "Invalid date") return null;
  
  

  if (date && moment.isMoment(date) && date.isValid()) {
    const timeParts = timeString.split(':');
    if (timeParts.length >= 2) {
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1], 10);
      const seconds = timeParts[2] ? parseInt(timeParts[2], 10) : 0;

      const combined = date.clone()
        .hour(hours)
        .minute(minutes)
        .second(seconds)
        .millisecond(0);
      
      
      return combined;
    }
  }
  
  
  const fallback = moment(timeString, 'HH:mm:ss');
  if (fallback.isValid()) {
    return fallback;
  }

  return null;
};

const PricesTypes = ['pricesFixedForm', 'pricesDailyForm', 'pricesWeeklyForm'];

const BookRentalItems = ({
  bookRentalItemsForm,
  changeFormFieldValue,
  handleSubmit,
  bookedData,
  isUpdate,
  businessOwner
}) => {

React.useEffect(() => {
  if (bookedData) {
  }
}, [bookedData]);
  
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();
  const [t, i18n] = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const confirm = React.useContext(ConfirmContext);
  const dispatch = useDispatch();

  const [isLoading, setLoading] = React.useState(false);
  const [rentalItems, setRentalItems] = React.useState(null);
  const [listOfCurrencies, setListOfCurrencies] = React.useState([]);
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  
  const [createRentalItemsBooking] = useMutation(CREATE_RENTAL_ITEMS_BOOKING);
  const [updateRentalItemsBooking] = useMutation(UPDATE_RENTAL_ITEMS_BOOKING);
  
  const ownerId = businessOwner.selectedBusinessOwner.ownerId || businessOwner.selectedBusinessOwner.id;
  const isInitialized = React.useRef(false);

  React.useLayoutEffect(() => {
    const updateSize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const rentalItemId = bookRentalItemsForm.values.rentalItemsId;

  const { loading, data, refetch } = useQuery(RENTAL_ITEM, {
    variables: {
      businessOwnerId: ownerId,
      rentalItemsId: rentalItemId || parseInt(id)
    },
    notifyOnNetworkStatusChange: true
  });

  React.useEffect(() => {
    refetch();
  }, []);

  const [availableUnitsData, setAvailableUnitsData] = React.useState(null);

  const rentalItemsId = bookRentalItemsForm.values.rentalItemsId;
 
  const initialStartingDate = bookedData && bookedData.fromDate
    ? bookedData.fromDate
    : bookedData && bookedData.multiDates
    ? bookedData.multiDates.startDate
    : moment();

  const {
    loading: isAvailableUnitsLoading,
    data: receivedAvailableUnitsData,
    refetch: refetchAvailableUnitsData
  } = useQuery(RENTAL_ITEMS_AVAILABLE_UNITS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      rentalItemsId: rentalItemsId || parseInt(id),
      businessOwnerId: ownerId,
      startPeriod: moment(initialStartingDate).startOf('month').format('YYYY-MM-DD'),
      endPeriod: moment(initialStartingDate).add(1, 'months').endOf('month').format('YYYY-MM-DD')
    }
  });

  React.useEffect(() => {
    if (!receivedAvailableUnitsData) return;
    const availableUnitsData = getReceivedRentalItemsBookingsDates(receivedAvailableUnitsData);
    setAvailableUnitsData(availableUnitsData);
  }, [receivedAvailableUnitsData]);


  React.useEffect(() => {
    if (bookedData && !isInitialized.current) {
      
      const initialValues = getInitialValues(bookedData);
      
      Object.keys(initialValues).forEach(key => {
        if (initialValues[key] !== undefined && initialValues[key] !== null) {
          changeFormFieldValue(key, initialValues[key]);
        }
      });
      
      isInitialized.current = true;
    }
  }, [bookedData, changeFormFieldValue]);

  React.useEffect(() => {
    if (data && data.rentalItems && data.rentalItems.length) {      
      const receivedRentalItems = data.rentalItems[0];
      const rentalItemsData = getReceivedRentalItemsData(receivedRentalItems);    
      rentalItemsData['pricesType'] = receivedRentalItems['pricesType'];
      if (receivedRentalItems.masterMinMaxHours) {
      changeFormFieldValue('minMaxHours', receivedRentalItems.masterMinMaxHours);
       }
      changeFormFieldValue('pricesType', receivedRentalItems['pricesType']);
      changeFormFieldValue('minDays',receivedRentalItems.minDays);
      changeFormFieldValue('maxDays',receivedRentalItems.maxDays)
      setListOfAvailableCurrencies(receivedRentalItems);
      setAddOns(rentalItemsData);
      setMaxAvailableUnits(rentalItemsData['generalForm'].availableUnits);
      setAvailableUnits(rentalItemsData);
      setRentalItems(rentalItemsData);
    }
  }, [data]);

  const setListOfAvailableCurrencies = receivedRentalItems => {
    const listOfAvailableCurrencies = getListOfAvailableCurrencies(
      Array.isArray(receivedRentalItems.prices) && receivedRentalItems.prices.length > 0
        ? receivedRentalItems.prices
        : receivedRentalItems.hourlyPrices
    );

    const selectedCurrency = bookRentalItemsForm.values.currency;
    setListOfCurrencies(listOfAvailableCurrencies);

    if (!listOfAvailableCurrencies.includes(selectedCurrency)) {
      changeFormFieldValue('currency', listOfAvailableCurrencies[0]);
    }
  };

  const setAddOns = rentalItems => {
    const previousAddOns = bookRentalItemsForm.values.addOns || [];
    const addOnsList = rentalItems.addOnsForm.addOns.map(item => {
      const theSameAddOn = previousAddOns.find(addOn => addOn.id === item.id);
      return {
        ...item,
        value: theSameAddOn ? theSameAddOn.value : 0,
        originalId: theSameAddOn ? theSameAddOn.originalId : null
      };
    });
    changeFormFieldValue('addOns', addOnsList);
  };

  const setAvailableUnits =  (rentalItems, previousAvailableUnits ) => {

    
    const availableUnits = getAvailableUnits(
      rentalItems,
      bookRentalItemsForm,
      bookRentalItemsForm.values.currency,
      previousAvailableUnits
    );
    changeFormFieldValue('availableUnits', availableUnits);
  };

React.useEffect(() => {
  const { startingDate, startTime, endTime, multiDates } = bookRentalItemsForm.values;

  const startTimeMoment = moment.isMoment(startTime) ? startTime : (startTime ? moment(startTime) : null);
  const endTimeMoment = moment.isMoment(endTime) ? endTime : (endTime ? moment(endTime) : null);
  const hasValidTimes = startTimeMoment && endTimeMoment && startTimeMoment.isValid() && endTimeMoment.isValid();

  try {
    if (!rentalItems) return;

    if (multiDates?.startDate && multiDates?.endDate && rentalItems?.pricesType !='pricesHourlyForm') {
 
      const computedUnits = handleUnits(rentalItems);

      handleMultiDateAvailableSeats(rentalItems);
      setAvailableUnits(rentalItems, computedUnits);
    }
   
    else if (rentalItems?.pricesType === 'pricesHourlyForm') {
      if (startingDate && hasValidTimes) {
        if (endTimeMoment.isBefore(startTimeMoment) && endTimeMoment.diff(startTimeMoment, 'hours') < -12) {
       
          return;
        }

        const computedUnits = handleUnitsHourly(rentalItems);
        handleHourlyAvailableSeats(rentalItems);
        setAvailableUnits(rentalItems, computedUnits);
      } else {
        setAvailableUnits(rentalItems, 0);
      }
    }
    // SINGLE DATE BOOKING 
    else if (startingDate) {
      const computedUnits = handleUnits(rentalItems);
      handleMultiDateAvailableSeats(rentalItems);
      setAvailableUnits(rentalItems, computedUnits);
    }
   
    else {
      setAvailableUnits(rentalItems, 0); 
    }

  } catch (error) {
    setAvailableUnits(rentalItems, 0);
  }
}, [
  rentalItems,
  availableUnitsData,
  bookRentalItemsForm.values.multiDates,
  bookRentalItemsForm.values.startingDate,
  bookRentalItemsForm.values.startTime,
  bookRentalItemsForm.values.endTime,
  bookRentalItemsForm.values.currency,
  rentalItems?.pricesType,
]);


  React.useEffect(() => {
    if (bookRentalItemsForm.values.startingDate && bookedData) {
      const currentDate = moment(bookRentalItemsForm.values.startingDate);
      const previousDate = moment(bookedData.startingDate || bookedData.multiDates?.startDate);
     
      if (!currentDate.isSame(previousDate, 'day')) {
        dispatch(change('bookRentalItemsForm', 'startTime', null));
        dispatch(change('bookRentalItemsForm', 'endTime', null));
      }
    }
  }, [bookRentalItemsForm.values.startingDate, bookedData, dispatch]);

  const setMaxAvailableUnits = seatsLeft => {
    const maxAvailableUnits = getMaxUnits(
      seatsLeft,
      bookedData,
      availableUnitsData,
      bookRentalItemsForm,
      
    );
    changeFormFieldValue('maxAvailableUnits', maxAvailableUnits);
  };

  const handleMultiDateAvailableSeats = rentalItems => {
    const multiDates = bookRentalItemsForm?.values?.multiDates;
    if (!rentalItems || !multiDates) return;

    const startDate = multiDates?.startDate;
    const endDate = multiDates?.endDate;
    if (!startDate || !endDate) return;

    const availableUnitsPerDate = availableUnitsData
      ? availableUnitsData.rentalItems?.[0]?.availableUnitsPerDate ?? []
      : [];

    const availableUnits = getMaxUnitsRentalItemsSeveralDays(
      availableUnitsPerDate,
      startDate,
      endDate,
      bookedData
    );

    if (availableUnits !== null) {
      setMaxAvailableUnits(availableUnits);
    } else {
      setMaxAvailableUnits(rentalItems?.generalForm?.availableUnits ?? 0);
    }
  };


const handleHourlyAvailableSeats = (rentalItems) => {
  const formValues = bookRentalItemsForm?.values;
  
  if (!rentalItems || !formValues) {
  
    return 0;
  }

  const startDate = formValues?.startingDate;
  const endDate = formValues?.multiDates?.endDate || startDate;
  const startTime = formValues?.startTime;
  const endTime = formValues?.endTime;

  if (!startDate || !startTime || !endTime) {

    return 0;
  }

  const availableUnitsPerDate = 
    availableUnitsData?.rentalItems?.[0]?.availableUnitsPerDate ?? [];
  

  

  const isUpdating = !!bookedData?.startingDate;
  
  const availableUnits = getMaxUnitsRentalItemsHourly(
    availableUnitsPerDate,
    startDate,
    endDate,
    bookedData,
    startTime,
    endTime,
    isUpdating 
  );

 

  if (availableUnits !== null && availableUnits !== undefined) {
    setMaxAvailableUnits(availableUnits);
  } else {
    const fallback = rentalItems?.generalForm?.availableUnits ?? 0;
    
    setMaxAvailableUnits(fallback);
  }

  return availableUnits;
};


const handleUnitsHourly = (rentalItems) => {
  const formValues = bookRentalItemsForm?.values;
  if (!rentalItems || !formValues) return 0;

  const startDate = formValues?.startingDate;
  const endDate = formValues?.multiDates?.endDate;
  const startTime = formValues?.startTime;
  const endTime = formValues?.endTime;

  if (!startDate || !endDate || !startTime || !endTime) return 0;

  const bookedUnits = getBookedUnitsHourly(
    bookedData,
    startDate,
    endDate,
    startTime,
    endTime
  );

  const unitsHourly = bookedUnits ?? 0;


  return unitsHourly;
};

const handleUnits = (rentalItems) => {
  const formValues = bookRentalItemsForm?.values;
  if (!rentalItems || !formValues) return 0;

  const startDate = formValues?.startingDate;
  const endDate = formValues?.multiDates?.endDate;

  if (!startDate || !endDate) return 0;

  const bookedUnits = getBookedUnits(
    bookedData,
    startDate,
    endDate,
  );

  const unitsHourly = bookedUnits ?? 0;


  return unitsHourly;
};





  const handlerSubmitWrapper = props => {
    if (bookRentalItemsForm.syncErrors) {
      const syncErrors = bookRentalItemsForm.syncErrors;  
      const errorsLength = Object.keys(syncErrors).length;

      if (syncErrors.isMaxUnitsErrors && errorsLength === 2) {
        const error = syncErrors.availableUnits['_error'];
        const errorSplit = error.split(' ');
        const errorLength = errorSplit.length;
        enqueueSnackbar(
          t('The number of available units can’t exceed', {
            number: errorSplit[errorLength - 1]
          }),
          {
            variant: 'warning'
          }
        );
      } else {
        enqueueSnackbar(t('Please fill in all the required fields'), {
          variant: 'warning'
        });
      }
    }

    handleSubmit(submit)(props);
  };

    const changeMonthHandler = date => {
      refetchAvailableUnitsData({
        rentalItemsId: rentalItemsId || parseInt(id),
        startPeriod: moment(date)
          .startOf('month')
          .format('YYYY-MM-DD'),
        endPeriod: moment(moment(date).add(1, 'months'))
          .endOf('month')
          .format('YYYY-MM-DD')
      });
    };

    const submit = () => {
        setLoading(true);
    
        const data = getPreparedBookRentalItemsData(
          rentalItems,
          bookRentalItemsForm,
        );
    
        if (isUpdate) {
          updateRentalItemsBooking({ variables: data }).then(
            () => {
              enqueueSnackbar(t('rental item booking has been successfully updated'), {
                variant: 'success'
              });
              history.push({
                pathname: '/bookings-rental-items-list'
              });
            },
            () => {
              setLoading(false);
              enqueueSnackbar(t('Something was wrong'), {
                variant: 'error'
              });
            }
          );
        } else {
          createRentalItemsBooking({ variables: data }).then(
            () => {
    
              enqueueSnackbar(t('rental item has been successfully booked'), {
                variant: 'success'
              });
              history.push({
                pathname: '/bookings-rental-items-list'
              });
            },
            () => {
              setLoading(false);
              enqueueSnackbar(t('Something was wrong'), {
                variant: 'error'
              });
            }
          );
        }
      };
  return (
    <>
      <AppHeader />
      <CircularLoader
        isLoading={loading || isLoading || isAvailableUnitsLoading}
        loaderClassName={classes.loaderClassName}
        className={classes.loaderWrapper}
      />

      {rentalItems && (
        <div className={classes.root}>
          <div className={classes.leftSide}>
            <Typography variant="h3" classes={{ root: classes.productCode }}>
              Product code: {rentalItems.generalForm.productCode}
            </Typography>

            <div
              className={classes.photo}
              style={{
                backgroundImage: `url(${rentalItems.generalForm.coverPhoto.image})`
              }}
            />

            <Typography variant="h2" className={classes.fontWeight}>
              {rentalItems.generalForm.title}
            </Typography>

            <div className={classes.listInfo}>
              <div className={classes.infoItem}>
                <LocationOnOutlinedIcon classes={{ root: classes.infoIcon }} />
                <Typography variant="body1">
                  {rentalItems.generalForm.availableUnits} Available Units
                </Typography>
              </div>
            </div>

            <Typography variant="body1" classes={{ root: classes.overview }}>
              {rentalItems.descriptionForm.description}
            </Typography>

            <BookInfoList
              activity={rentalItems}
              currency={bookRentalItemsForm.values.currency}
              defaultExpanded={true}
            />
          </div>

          <div className={classes.rightSide}>
            <form onSubmit={handlerSubmitWrapper} className={classes.bookWrapper}>
              <Typography
                variant="h1"
                className={classNames(classes.fontWeight, classes.inputIndent)}
              >
                {isUpdate ? t('Update Rental Items Booking') : t('Book Rental Items')}
              </Typography>

              <Field
                name="currency"
                className={classNames(classes.currencyInput, classes.sectionIndent)}
                list={listOfCurrencies}
                label="Select currency"
                component={DropDown}
              />

              {PricesTypes.includes(rentalItems.pricesType) && (
                <Field
                  name="multiDates"
                  label="Select date *"
                  numberOfMonths={windowWidth > 1010 ? 2 : windowWidth <= 750 ? 1 : 2}
                  daySize={windowWidth > 600 ? 38 : 41}
                  initialFocus="startDate"
                  dateRangePickerClass="dateRangeOpenedPicker"
                  inputClassName={classes.disableInputFocus}
                  component={MultiDatePickerCalendar}
                  changeMonthsCallback={changeMonthHandler}
                  disabledDates={getDisabledDates(
                    availableUnitsData,
                    bookedData && bookedData.multiDates
                  )}
                />
                
              )}

              {!PricesTypes.includes(rentalItems.pricesType) && (
                <Field
                  name="startingDate"
                  label="Starting date *"
                  numberOfMonths={windowWidth > 1010 ? 2 : windowWidth <= 750 ? 1 : 2}
                  daySize={windowWidth > 600 ? 38 : 41}
                  component={SingleDatePicker}
                  changeMonthsCallback={changeMonthHandler}
                  disabledDates={getDisabledDatesWithTime(
                    availableUnitsData,
                    bookedData && bookedData.startingDate
                  )}
                />
              )}

              <div className={classes.timePickerRow}>
                {!PricesTypes.includes(rentalItems.pricesType) && (
                  <Field
                    name="startTime"
                    label="Starting time *"
                    component={CustomTimePicker}
                    selectedDate={bookRentalItemsForm.values.startingDate}
                    availableUnitsData={availableUnitsData?.rentalItems}
                    className={classNames(classes.currencyInput, classes.sectionIndent)}
                  />
                )}

                {!PricesTypes.includes(rentalItems.pricesType) && (
                  <Field
                    name="endTime"
                    label="Ending time *"
                    component={CustomTimePicker}
                    selectedDate={bookRentalItemsForm.values.startingDate}
                    startTimeValue={bookRentalItemsForm.values.startTime}
                    availableUnitsData={availableUnitsData?.rentalItems}
                    className={classNames(classes.currencyInput, classes.sectionIndent)}
                  />
                )}
              </div>

              {!!bookRentalItemsForm.values.availableUnits > 0 && (
                <FieldArray
                  name="availableUnits"
                  title="Number Of Units *"
                  component={UnitsInputField}
                  currency={bookRentalItemsForm.values.currency}
                />
              )}

              {!!bookRentalItemsForm.values.addOns.length && (
                <FieldArray
                  name="addOns"
                  title="Add-ons"
                  currency={bookRentalItemsForm.values.currency}
                  component={AddOnsList}
                  addOnDescClass={classes.addOnDescClass}
                  addOnTitle={classes.addOnTitle}
                />
              )}

              <GuestInformation />

              <div className={classes.paymentWrapper}>
                <Typography variant="h2" className={classes.payment}>
                  {t('Payment')}
                </Typography>
                <Field
                  name="pay"
                  className={classes.paymentField}
                  disabledDays={['Pay on arrival']}
                  isDisabledChange={true}
                  component={CheckboxButtonsGroup}
                />
              </div>

              <RentalBookingSummary
                rentalItems={rentalItems}
                bookRentalItems={bookRentalItemsForm.values}
                generalSectionTitle="Rental Items"
              />

              <FormActionButtons
                cancelPath={isUpdate ? '/bookings-rental-items-list' : '/rental-items'}
                actionLabel={isUpdate ? 'Update' : 'Book'}
              />
            </form>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = state => {
  return {
    bookRentalItemsForm: state.form.bookRentalItemsForm,
    businessOwner: state.businessOwner
  };
};

const mapDispatchToProps = dispatch => ({
  changeFormFieldValue: (field, value) => {
    dispatch(change('bookRentalItemsForm', field, value));
  }
});

export default reduxForm({
  form: 'bookRentalItemsForm',
  destroyOnUnmount: true,
  touchOnBlur: false,
  initialValues: getInitialValues(),
  validate: (values, props) => validate(values, props)
})(connect(mapStateToProps, mapDispatchToProps)(BookRentalItems));